import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, ArrowLeft, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChatbotManager = () => {
  const [faqs, setFaqs] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    keywords: '',
    category: 'General'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchFaqs();
    initializeDefaultFaqs();
  }, [navigate]);

  const initializeDefaultFaqs = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await axios.get(`${API}/chatbot/faqs`);
      if (response.data.length === 0) {
        // Add default FAQs
        const defaultFaqs = [
          {
            question: "Who is Gangadhar Som?",
            answer: "Gangadhar Som is a renowned professional artist with over 40 years of experience in fine arts and the South Indian film industry. He inherited his talent from his father Sri Gangadhar, a celebrated artist and film publicity designer. He is the creator of 46 Lord Hanuman life history paintings at Karya Siddhi Hanuman Temple, Dallas, Texas. Honored with the title 'Datta Peetha Asthan Chitrakara'.",
            keywords: "artist,who,biography,about",
            category: "About Artist"
          },
          {
            question: "What is the Dallas Hanuman project?",
            answer: "The Dallas Hanuman project consists of 46 monumental oil paintings (5x8 feet each) depicting the complete life history of Lord Hanuman. Created for the Karya Siddhi Hanuman Temple in Dallas, Texas. These are among the artist's most celebrated temple works.",
            keywords: "hanuman,dallas,temple,project,46 paintings",
            category: "Notable Works"
          },
          {
            question: "Do you accept commissions?",
            answer: "Yes! We accept commissions for devotional paintings, portraits (person, family, and pet), and custom artwork. Please use the contact form to reach out with your requirements.",
            keywords: "commission,custom,order,purchase,buy,portrait",
            category: "Commissions"
          },
          {
            question: "What mediums does the artist work in?",
            answer: "Gangadhar Som is a master of multiple artistic mediums including oil paintings, acrylics, watercolors, spray paintings, and digital illustrations using Adobe Photoshop and Illustrator. He holds a Diploma in Fine Arts (1984) and Post Diploma (1985) from the Government College of Arts & Crafts, Madras.",
            keywords: "medium,oil,canvas,paint,materials,acrylic,watercolor,digital",
            category: "Artistic Process"
          },
          {
            question: "Where can I see the artwork in person?",
            answer: "The 46 Lord Hanuman paintings are permanently displayed at the Karya Siddhi Hanuman Temple in Dallas, Texas. The Nava Nandi Series can be seen at Mahanandi Temple, Andhra Pradesh. Lord Rama wall murals are at Sri Ganapathi Sachchidananda Ashrama, Ayodhya. For other works and exhibitions, please contact us.",
            keywords: "visit,see,location,exhibition,gallery,temple",
            category: "Exhibitions"
          }
        ];

        for (const faq of defaultFaqs) {
          const formData = new FormData();
          formData.append('question', faq.question);
          formData.append('answer', faq.answer);
          formData.append('keywords', faq.keywords);
          formData.append('category', faq.category);

          await axios.post(`${API}/chatbot/faqs`, formData, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
        }
        fetchFaqs();
      }
    } catch (error) {
      console.error('Error initializing FAQs:', error);
    }
  };

  const fetchFaqs = async () => {
    try {
      const response = await axios.get(`${API}/chatbot/faqs`);
      setFaqs(response.data);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    }
  };

  const handleOpenModal = (faq = null) => {
    if (faq) {
      setEditingFaq(faq);
      setFormData({
        question: faq.question,
        answer: faq.answer,
        keywords: faq.keywords.join(', '),
        category: faq.category
      });
    } else {
      setEditingFaq(null);
      setFormData({
        question: '',
        answer: '',
        keywords: '',
        category: 'General'
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingFaq(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const token = localStorage.getItem('admin_token');
    const formDataToSend = new FormData();
    
    Object.keys(formData).forEach(key => {
      formDataToSend.append(key, formData[key]);
    });

    try {
      if (editingFaq) {
        await axios.put(`${API}/chatbot/faqs/${editingFaq.id}`, formDataToSend, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        toast.success('FAQ updated successfully');
      } else {
        await axios.post(`${API}/chatbot/faqs`, formDataToSend, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        toast.success('FAQ created successfully');
      }
      handleCloseModal();
      fetchFaqs();
    } catch (error) {
      console.error('Error saving FAQ:', error);
      toast.error('Failed to save FAQ');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (faqId) => {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) {
      return;
    }

    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(`${API}/chatbot/faqs/${faqId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('FAQ deleted successfully');
      fetchFaqs();
    } catch (error) {
      console.error('Error deleting FAQ:', error);
      toast.error('Failed to delete FAQ');
    }
  };

  const categories = [...new Set(faqs.map(faq => faq.category))];

  return (
    <div className="min-h-screen pt-32 pb-24" data-testid="chatbot-manager-page">
      <div className="max-w-screen-xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Header */}
        <div className="flex items-center justify-between mb-12">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard">
              <Button variant="outline">
                <ArrowLeft size={16} className="mr-2" />
                Back to Dashboard
              </Button>
            </Link>
            <div>
              <h1 className="font-serif text-4xl md:text-5xl font-light tracking-tight">
                Chatbot FAQ Manager
              </h1>
              <p className="font-sans text-muted-foreground mt-2">
                Manage questions and answers for the chatbot
              </p>
            </div>
          </div>
          <Button
            onClick={() => handleOpenModal()}
            className="bg-accent hover:bg-accent/90"
            data-testid="add-faq-button"
          >
            <Plus size={16} className="mr-2" />
            Add FAQ
          </Button>
        </div>

        {/* FAQs by Category */}
        {categories.map(category => (
          <div key={category} className="mb-8">
            <h2 className="font-serif text-2xl font-light mb-4 flex items-center gap-2">
              <MessageCircle size={20} className="text-accent" />
              {category}
            </h2>
            <div className="grid gap-4">
              {faqs.filter(faq => faq.category === category).map(faq => (
                <motion.div
                  key={faq.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-card rounded-lg p-6 border border-border"
                >
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                      <h3 className="font-sans font-semibold text-lg mb-2">
                        {faq.question}
                      </h3>
                      <p className="font-sans text-sm text-foreground/80 mb-3 leading-relaxed">
                        {faq.answer}
                      </p>
                      {faq.keywords.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {faq.keywords.map((keyword, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-accent/10 text-accent text-xs rounded-full"
                            >
                              {keyword}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleOpenModal(faq)}
                      >
                        <Edit size={14} />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDelete(faq.id)}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        ))}

        {faqs.length === 0 && (
          <div className="text-center py-24">
            <MessageCircle size={48} className="mx-auto mb-4 text-muted-foreground" />
            <p className="font-sans text-muted-foreground mb-4">No FAQs yet. Add your first question!</p>
            <Button onClick={() => handleOpenModal()} className="bg-accent hover:bg-accent/90">
              <Plus size={16} className="mr-2" />
              Add FAQ
            </Button>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={handleCloseModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-light">
              {editingFaq ? 'Edit FAQ' : 'Add New FAQ'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="question">Question *</Label>
              <Input
                id="question"
                value={formData.question}
                onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                placeholder="What is your question?"
                required
              />
            </div>

            <div>
              <Label htmlFor="answer">Answer *</Label>
              <Textarea
                id="answer"
                value={formData.answer}
                onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                placeholder="Provide a detailed answer..."
                rows={6}
                required
              />
            </div>

            <div>
              <Label htmlFor="keywords">Keywords (comma-separated)</Label>
              <Input
                id="keywords"
                value={formData.keywords}
                onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                placeholder="commission, buy, purchase, order"
              />
              <p className="text-sm text-muted-foreground mt-1">
                Keywords help the chatbot match user queries to this answer
              </p>
            </div>

            <div>
              <Label htmlFor="category">Category</Label>
              <Input
                id="category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                placeholder="General"
              />
            </div>

            <div className="flex justify-end gap-4">
              <Button type="button" variant="outline" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-accent hover:bg-accent/90"
              >
                {isSubmitting ? 'Saving...' : (editingFaq ? 'Update' : 'Create')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChatbotManager;